import React from "react";
import {
  Sparkles,
  Crown,
  ChevronRight,
  TrendingUp,
  Scale3d,
  Activity,
} from "lucide-react";
import { ProgressRing } from "../ProgressRing";
import { aiVerdict, offers } from "../../data/offerData";

const tone = {
  low: { dot: "bg-indigo-500", chip: "bg-indigo-50 text-indigo-700 border-indigo-200", label: "Strength" },
  med: { dot: "bg-amber-500", chip: "bg-amber-50 text-amber-700 border-amber-200", label: "Watch" },
  high: { dot: "bg-rose-500", chip: "bg-rose-50 text-rose-700 border-rose-200", label: "Action" },
};

export const AIRecommendationEngine = () => {
  const recommended = offers.find((o) => o.id === aiVerdict.recommended);

  return (
    <section className="rounded-3xl overflow-hidden border border-slate-200 cf-shadow-lg" data-testid="ai-recommendation-engine">
      {/* Hero strip */}
      <div className="relative bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-900/40 text-white p-6">
        <div className="absolute inset-0 opacity-40 pointer-events-none">
          <div className="absolute -top-24 -right-24 w-80 h-80 cf-blob" />
        </div>
        <div className="relative flex items-start gap-5">
          <div className="w-12 h-12 rounded-2xl bg-indigo-500/20 border border-indigo-400/40 flex items-center justify-center shrink-0">
            <Sparkles size={20} className="text-indigo-300" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="text-[10.5px] uppercase tracking-[0.22em] text-indigo-300 font-bold">ChainFlow Offer Intelligence</p>
              <span className="text-[10.5px] uppercase tracking-[0.16em] text-slate-400">model v3.1 · 91% confidence</span>
            </div>
            <h2 className="font-display text-[22px] font-bold mt-1 leading-tight">Recommended buyer · {aiVerdict.recommendedBuyer}</h2>
            <p className="text-[13px] text-slate-300 mt-1.5 leading-relaxed max-w-[680px]">{aiVerdict.reason}</p>

            <div className="mt-4 grid grid-cols-3 gap-3 max-w-[640px]">
              <KpiTile label="Risk-weighted value" value={aiVerdict.weightedValue} icon={Scale3d} />
              <KpiTile label="Exchange probability" value={`${aiVerdict.exchangeProb}%`} icon={Activity} />
              <KpiTile label="Headline offer" value={aiVerdict.recommendedOffer} icon={TrendingUp} />
            </div>
          </div>
          <button className="hidden md:inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white text-[12.5px] font-semibold shadow-[0_8px_24px_rgba(99, 102, 241,0.35)] transition-all">
            <Crown size={13} /> Accept recommendation
          </button>
        </div>
      </div>

      {/* Body — insights + factor weighting */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 bg-white">
        {/* Insights list */}
        <div className="lg:col-span-3 border-r border-slate-100">
          <div className="px-6 pt-5 pb-3 border-b border-slate-100 flex items-center justify-between">
            <p className="text-[10.5px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Reasoning trail</p>
            <button data-testid="ai-trail-explain" className="text-[12px] font-semibold text-indigo-700 hover:underline">How we score</button>
          </div>
          <ul>
            {aiVerdict.insights.map((ins, i) => {
              const t = tone[ins.tone];
              return (
                <li
                  key={i}
                  data-testid={`ai-insight-${i}`}
                  className="px-6 py-4 border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60 transition-colors flex items-start gap-3"
                >
                  <span className={`mt-1.5 w-2 h-2 rounded-full ${t.dot}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <p className="text-[13.5px] font-semibold text-slate-900">{ins.title}</p>
                      <span className={`text-[10px] font-semibold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${t.chip}`}>{t.label}</span>
                    </div>
                    <p className="text-[12.5px] text-slate-600 leading-relaxed">{ins.detail}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Factor weighting */}
        <div className="lg:col-span-2 p-6">
          <p className="text-[10.5px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Factor weighting</p>
          <p className="text-[12px] text-slate-500 mt-1">How the score for {aiVerdict.recommendedBuyer} was computed.</p>

          <ul className="mt-4 space-y-3.5">
            {aiVerdict.factors.map((f) => (
              <li key={f.label} data-testid={`factor-${f.label}`}>
                <div className="flex items-center justify-between text-[11.5px]">
                  <span className="text-slate-700 font-medium">{f.label}</span>
                  <span className="font-mono-cf text-slate-900">
                    <b>{f.score}</b><span className="text-slate-400"> · {f.weight}%</span>
                  </span>
                </div>
                <div className="mt-1.5 relative h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-indigo-500 to-indigo-400"
                    style={{ width: `${f.score}%` }}
                  />
                  <div
                    className="absolute top-1/2 -translate-y-1/2 w-[2px] h-3 rounded-full bg-slate-900/60"
                    style={{ left: `${f.weight * 4}%` }}
                    title="Weight marker"
                  />
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-5 pt-4 border-t border-slate-100 flex items-center gap-3">
            <ProgressRing value={aiVerdict.exchangeProb} size={56} stroke={6} color="#6366F1" label={`${aiVerdict.exchangeProb}`} />
            <div>
              <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Exchange forecast</p>
              <p className="text-[12.5px] text-slate-900 font-medium">3 wks · model confidence 91%</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const KpiTile = ({ label, value, icon: Icon }) => (
  <div className="rounded-xl bg-white/[0.06] border border-white/[0.08] px-3.5 py-3 backdrop-blur-md">
    <div className="flex items-center gap-1.5 text-indigo-300/90">
      <Icon size={12} />
      <p className="text-[9.5px] uppercase tracking-[0.18em] font-bold">{label}</p>
    </div>
    <p className="font-display text-[18px] font-bold text-white mt-1.5 leading-none">{value}</p>
  </div>
);
